/**
 * ApplicationsTab — admin review pipeline for public "Join Our Church"
 * membership applications submitted at /church/:churchId/join.
 *
 * Approve → creates a `church_members` row + dispatches the welcome email.
 * Reject  → captures a reason and preserves the record for audit.
 *
 * PII on the wire is decrypted server-side; the tab renders it plainly
 * to admins only. Rejection reason and consent audit are surfaced so
 * reviewers can act with full context.
 */
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  ClipboardList, Loader2, Check, X, Mail, Phone, MapPin, Calendar,
  User, Sparkles, Heart, ShieldCheck, Copy, Link as LinkIcon, Church,
} from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const HEARD_ABOUT_LABEL = {
  friend_family: 'Friend or family',
  social_media: 'Social media',
  google_search: 'Google search',
  event_outreach: 'Event / outreach',
  radio_tv: 'Radio or TV',
  walked_in: 'Walked in',
  other: 'Other',
};

export default function ApplicationsTab({ churchId, church }) {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState('pending');
  const [selected, setSelected] = useState(null); // full application for detail dialog
  const [rejecting, setRejecting] = useState(null); // application being rejected
  const [rejectReason, setRejectReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['membership-applications', churchId, status],
    queryFn: () => api(`/api/admin/churches/${churchId}/membership-applications?status=${status}`),
  });

  // A notification names the application it is about (?application=<id>), so
  // following the bell opens that person's record instead of a list the
  // reviewer then has to search. The id is dropped from the URL once opened.
  const wantedId = searchParams.get('application');
  React.useEffect(() => {
    if (!wantedId) return;
    let cancelled = false;
    api(`/api/admin/churches/${churchId}/membership-applications/${wantedId}`)
      .then((app) => {
        if (cancelled) return;
        if (app?.status && app.status !== status) setStatus(app.status);
        setSelected(app);
      })
      .catch((e) => toast.error(e.message || 'That application could not be opened'))
      .finally(() => {
        if (cancelled) return;
        const next = new URLSearchParams(searchParams);
        next.delete('application');
        setSearchParams(next, { replace: true });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedId, churchId]);

  const approve = useMutation({
    mutationFn: (appId) => api(
      `/api/admin/churches/${churchId}/membership-applications/${appId}/approve`,
      { method: 'POST' },
    ),
    onSuccess: (r) => {
      const emailNote = r?.email_dispatch?.sent
        ? 'Welcome email sent.'
        : `Welcome email skipped (${r?.email_dispatch?.reason || 'no email'}).`;
      toast.success(`Approved · new member created. ${emailNote}`);
      qc.invalidateQueries({ queryKey: ['membership-applications', churchId] });
      qc.invalidateQueries({ queryKey: ['church-members', churchId] });
      setSelected(null);
    },
    onError: (e) => toast.error(e.message || 'Approval failed'),
  });

  const reject = useMutation({
    mutationFn: ({ appId, reason }) => api(
      `/api/admin/churches/${churchId}/membership-applications/${appId}/reject`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),
    onSuccess: () => {
      toast.success('Application rejected');
      qc.invalidateQueries({ queryKey: ['membership-applications', churchId] });
      setRejecting(null);
      setRejectReason('');
      setSelected(null);
    },
    onError: (e) => toast.error(e.message || 'Rejection failed'),
  });

  const items = data?.items || [];
  const counts = data?.counts || { pending: 0, approved: 0, rejected: 0 };

  const publicUrl = useMemo(
    () => `${window.location.origin}/church/${churchId}/join`,
    [churchId],
  );
  const copyPublicUrl = () => {
    navigator.clipboard.writeText(publicUrl)
      .then(() => toast.success('Public link copied'))
      .catch(() => toast.error('Copy failed'));
  };

  return (
    <div className="space-y-4" data-testid="applications-tab">
      {/* Header + share link */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-violet-600" /> Membership applications
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Anyone with this link can apply to join {church?.name || 'the church'}. PII is encrypted at rest.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-600 flex items-center gap-1">
            <LinkIcon className="h-3 w-3" /> {publicUrl}
          </div>
          <Button
            size="sm" variant="outline" onClick={copyPublicUrl}
            data-testid="applications-copy-link"
          >
            <Copy className="h-3.5 w-3.5 mr-1" /> Copy
          </Button>
        </div>
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-2">
        {['pending', 'approved', 'rejected'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            data-testid={`applications-filter-${s}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              status === s
                ? 'bg-violet-600 text-white border-violet-600'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
            <span className={`ml-1 text-[10px] ${status === s ? 'opacity-90' : 'text-slate-400'}`}>
              {counts[s] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="py-8 text-center text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin mx-auto" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg">
          No {status} applications.
        </div>
      ) : (
        <div className="space-y-2" data-testid="applications-list">
          {items.map((app) => (
            <ApplicationRow
              key={app.id}
              app={app}
              onOpen={() => setSelected(app)}
              onApprove={() => approve.mutate(app.id)}
              onReject={() => { setRejecting(app); setRejectReason(''); }}
              approving={approve.isPending && approve.variables === app.id}
            />
          ))}
        </div>
      )}

      {/* Detail dialog */}
      {selected && (
        <ApplicationDetailDialog
          app={selected}
          open={Boolean(selected)}
          onClose={() => setSelected(null)}
          onApprove={() => approve.mutate(selected.id)}
          onReject={() => { setRejecting(selected); setRejectReason(''); }}
          approving={approve.isPending}
        />
      )}

      {/* Reject dialog */}
      {rejecting && (
        <Dialog open onOpenChange={(v) => { if (!v) setRejecting(null); }}>
          <DialogContent data-testid="applications-reject-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle>Reject application</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              Reject <span className="font-semibold">{rejecting.full_name}</span>&apos;s application?
              You can optionally add a note (kept for audit — the applicant is not notified).
            </p>
            <Textarea
              data-testid="applications-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={3}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                data-testid="applications-reject-confirm"
                variant="destructive"
                disabled={reject.isPending}
                onClick={() => reject.mutate({ appId: rejecting.id, reason: rejectReason })}
              >
                {reject.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                Reject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


function ApplicationRow({ app, onOpen, onApprove, onReject, approving }) {
  const contact = app.email || app.phone || '—';
  return (
    <div
      data-testid={`application-row-${app.id}`}
      className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3 hover:border-slate-300"
    >
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onOpen}>
        <div className="text-sm font-medium text-slate-800 truncate">{app.full_name}</div>
        <div className="text-[11px] text-slate-500 truncate">
          {contact}
          {app.heard_about && (
            <span className="ml-2">
              · <Sparkles className="inline-block h-2.5 w-2.5" /> {HEARD_ABOUT_LABEL[app.heard_about] || app.heard_about}
            </span>
          )}
          <span className="ml-2 text-slate-400">
            · {app.created_at ? new Date(app.created_at).toLocaleDateString() : ''}
          </span>
        </div>
      </div>
      <StatusBadge status={app.status} />
      {app.status === 'pending' && (
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            onClick={onApprove}
            disabled={approving}
            data-testid={`application-approve-${app.id}`}
          >
            {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onReject}
            data-testid={`application-reject-${app.id}`}
            className="border-rose-300 text-rose-600 hover:bg-rose-50"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}


function StatusBadge({ status }) {
  const map = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-emerald-100 text-emerald-800',
    rejected: 'bg-rose-100 text-rose-800',
  };
  return <Badge className={`text-[10px] capitalize ${map[status] || ''}`}>{status}</Badge>;
}


function ApplicationDetailDialog({ app, open, onClose, onApprove, onReject, approving }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="application-detail-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-4 w-4 text-violet-600" /> {app.full_name}
            <StatusBadge status={app.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <Field icon={Mail} label="Email" value={app.email || '—'} />
          <Field icon={Phone} label="Phone" value={
            app.phone ? `${app.phone_country ? app.phone_country + ' ' : ''}${app.phone}` : '—'
          } />
          <Field icon={Calendar} label="Date of birth" value={app.date_of_birth || '—'} />
          <Field icon={MapPin} label="Address" value={app.address || '—'} multiline />
          <Field icon={Sparkles} label="How they heard about us"
                 value={HEARD_ABOUT_LABEL[app.heard_about] || app.heard_about || '—'} />
          {app.marital_status && <Field icon={Heart} label="Marital status" value={app.marital_status} />}
          {app.previous_church && (
            <Field icon={Church} label="Previous church" value={app.previous_church} />
          )}
          {app.visitor_id && (
            <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs flex items-center gap-2"
                 data-testid="application-visitor-link">
              <LinkIcon className="h-3.5 w-3.5 text-emerald-700" />
              <span className="text-emerald-800">
                This person already planned a visit — their visitor record is linked.
              </span>
            </div>
          )}
          {app.testimony && (
            <div className="rounded border border-violet-200 bg-violet-50 p-3 text-sm"
                 data-testid="application-testimony">
              <div className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold flex items-center gap-1 mb-1">
                <Sparkles className="h-3 w-3" /> Testimony
              </div>
              <p className="text-slate-700 whitespace-pre-wrap">{app.testimony}</p>
            </div>
          )}
          {app.spouse_name && <Field label="Spouse" value={app.spouse_name} />}
          {app.family_notes && <Field label="Family notes" value={app.family_notes} multiline />}
          {app.children?.length ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                 data-testid="application-children">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                Children registered with this application
              </p>
              <ul className="mt-1.5 space-y-1 text-sm text-amber-950">
                {app.children.map((c, i) => (
                  <li key={i} data-testid={`application-child-${i}`}>
                    {c.full_name}
                    {c.date_of_birth ? <span className="text-amber-800/70"> · born {c.date_of_birth}</span> : null}
                    {c.notes ? <span className="text-amber-800/70"> · {c.notes}</span> : null}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-amber-900/80">
                {app.guardian_consent
                  ? 'The parent or guardian consented on their behalf. Children are not enrolled by this form — enrol them yourself once you have met the family.'
                  : 'No guardian consent was recorded. Check with the parent before contacting anyone about the children.'}
              </p>
            </div>
          ) : null}
          {app.source_code ? (
            <Field label="Came from" value={`QR code ${app.source_code}`}
                   data-testid="application-source-code" />
          ) : null}
          {app.baptism && app.baptism.baptized && (
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
              <div className="font-semibold text-slate-700">Baptism</div>
              <div>Baptised: <span className="text-slate-600">{app.baptism.baptism_date || 'date unknown'}</span></div>
              {app.baptism.baptism_place && <div>Place: {app.baptism.baptism_place}</div>}
              {app.baptism.baptized_by && <div>By: {app.baptism.baptized_by}</div>}
            </div>
          )}
          {app.prayer_request && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold flex items-center gap-1 mb-1">
                <Heart className="h-3 w-3" /> Prayer request
              </div>
              <p className="text-slate-700 whitespace-pre-wrap">{app.prayer_request}</p>
            </div>
          )}

          {app.consent && (
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-500 space-y-0.5">
              <div className="font-semibold text-slate-700 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Consent audit
              </div>
              <div>Version {app.consent.version} · region {app.consent.region}</div>
              <div>Accepted at {app.consent.accepted_at
                ? new Date(app.consent.accepted_at).toLocaleString()
                : '—'}</div>
              {app.consent.ip && <div>From IP {app.consent.ip}</div>}
            </div>
          )}
          {app.status === 'rejected' && app.rejection_reason && (
            <div className="rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
              <div className="font-semibold mb-0.5">Rejection reason</div>
              {app.rejection_reason}
            </div>
          )}
          {app.status === 'approved' && app.member_id && (
            <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
              Approved · linked to member <span className="font-mono">{app.member_id}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {app.status === 'pending' && (
            <>
              <Button
                variant="outline"
                onClick={onReject}
                data-testid="application-detail-reject"
                className="border-rose-300 text-rose-600 hover:bg-rose-50"
              >
                <X className="h-3.5 w-3.5 mr-1" /> Reject
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                disabled={approving}
                onClick={onApprove}
                data-testid="application-detail-approve"
              >
                {approving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                Approve & welcome
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ icon: Icon, label, value, multiline }) {
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="h-3.5 w-3.5 text-slate-400 mt-1 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
        <div className={`text-sm text-slate-800 ${multiline ? 'whitespace-pre-wrap' : 'truncate'}`}>{value}</div>
      </div>
    </div>
  );
}
